import { exists } from "@banjoanton/utils";
import { Chess, type Square } from "chess.js";
import {
    type ChessColor,
    ChessPiece,
    type ChessPiece as ChessPieceType,
} from "../models/base-model";
import type { Move } from "../models/move-model";

export type PieceData = {
    type: ChessPieceType;
    color: ChessColor;
    square: Square;
    value: number;
};

/**
 * Read-only wrapper for querying chess board state.
 *
 * Does not modify the board - all methods are pure queries.
 * Create via static factory methods: `from()`, `fromFen()`, or `fromMove()`.
 *
 * @example
 * const board = BoardQuery.fromMove(move);
 * const attackers = board.attackers("e4");
 * const whiteKing = board.findKing("w");
 */
export class BoardQuery {
    private engine: Chess;

    private constructor(fen: string) {
        this.engine = new Chess();
        this.engine.load(fen);
    }

    /** Creates a snapshot from a Chess instance (copies current state). */
    static from(engine: Chess): BoardQuery {
        return new BoardQuery(engine.fen());
    }

    static fromFen(fen: string): BoardQuery {
        return new BoardQuery(fen);
    }

    /** Creates query for the board state after the move was played. */
    static fromMove(move: Move): BoardQuery {
        return new BoardQuery(move.fenAfter);
    }

    get fen(): string {
        return this.engine.fen();
    }

    get raw(): Chess {
        return this.engine;
    }

    get turn(): ChessColor {
        return this.engine.turn();
    }

    /** The color that just played (opposite of turn). */
    get lastMoved(): ChessColor {
        return this.turn === "w" ? "b" : "w";
    }

    inCheck(): boolean {
        return this.engine.inCheck();
    }

    pieces(): PieceData[];
    pieces(color: ChessColor): PieceData[];
    pieces(color?: ChessColor): PieceData[] {
        const allPieces = this.engine
            .board()
            .flat()
            .filter(exists)
            .map((sq) => this.toPieceData(sq));

        if (color === undefined) {
            return allPieces;
        }

        return allPieces.filter((p) => p.color === color);
    }

    findPiece(type: ChessPieceType, color: ChessColor): PieceData[] {
        return this.pieces(color).filter((p) => p.type === type);
    }

    findKing(color: ChessColor): PieceData {
        const king = this.findPiece("k", color)[0];
        if (!king) {
            throw new Error(`King not found for color ${color}`);
        }
        return king;
    }

    pieceAt(square: Square): PieceData | undefined {
        const piece = this.engine.get(square);
        if (!piece) {
            return undefined;
        }
        return this.toPieceData({ ...piece, square });
    }

    /**
     * Gets pieces attacking a square.
     * If square has a piece, returns opponent's pieces attacking it.
     * If square is empty, returns pieces of the side to move that attack it.
     */
    attackers(square: Square): PieceData[] {
        const pieceAtSquare = this.pieceAt(square);
        const attackingColor = pieceAtSquare
            ? this.oppositeColor(pieceAtSquare.color)
            : this.turn;

        return this.engine
            .attackers(square, attackingColor)
            .map((s) => this.pieceAt(s))
            .filter(exists);
    }

    /**
     * Gets pieces defending a square.
     * If square has a piece, returns same-color pieces defending it.
     * If square is empty, returns pieces of the side to move that control it.
     */
    defenders(square: Square): PieceData[] {
        const pieceAtSquare = this.pieceAt(square);
        const defendingColor = pieceAtSquare ? pieceAtSquare.color : this.turn;

        return this.engine
            .attackers(square, defendingColor)
            .map((s) => this.pieceAt(s))
            .filter(exists);
    }

    pieceTypesAt(squares: Square[]): ChessPieceType[] {
        return squares
            .map((s) => this.pieceAt(s))
            .filter(exists)
            .map((p) => p.type);
    }

    private oppositeColor(color: ChessColor): ChessColor {
        return color === "w" ? "b" : "w";
    }

    private toPieceData(sq: {
        type: string;
        color: string;
        square: Square;
    }): PieceData {
        const type = sq.type as ChessPieceType;
        return {
            type,
            color: sq.color as ChessColor,
            square: sq.square,
            value: ChessPiece.value(type),
        };
    }
}
